import curry from "../../lib/core/functional/curry";

import { vote } from "../../transport/voting";

import { emitErrorFactory, clearFeedback, EVENTS } from "./_common";
import { renderExtraField } from "./templates";
import { getVoteRecord, setVoteRecord } from "./storage";
import * as pollActions from "./pollActions";

const renderExtraFieldsFactory = curry(renderExtraField);

function renderExtraFields(container, jsonSchema) {
    if (!("properties" in jsonSchema)) return;

    const requiredFields = jsonSchema.required || [];
    const fields = Object.entries(jsonSchema.properties);
    const renderedFields = fields.map(renderExtraFieldsFactory(requiredFields)).join("");
    container.innerHTML = renderedFields;
}

function prepareExtraFields(form) {
    const extraFieldsHolder = form.querySelector(".listicle-item-voting-extrafields");
    const rawExtraFields = extraFieldsHolder.dataset.extraFields;

    try {
        const extraFields = JSON.parse(unescape(rawExtraFields));
        renderExtraFields(extraFieldsHolder, extraFields);
    } catch (error) {
        console.log(error);
    }
}

function elementsValueReducer(store, element) {
    switch (element.type) {
        case "radio":
            if (element.checked) {
                store[element.name] = element.value;
            }
            break;
        case "chechbox":
            store[element.name] = element.checked;
            break;
        default:
            store[element.name] = element.value;
    }
    return store;
}

function getFormBody(listicle) {
    const elements = Array.from(listicle.querySelectorAll("input,select"));
    return elements.reduce(elementsValueReducer, {});
}

function getInputValue(element) {
    return element.value;
}

function reactionReducer(store, value) {
    store[value] = {};
    return store;
}

function reactionsFactory(selected, listicle) {
    const allOptions = Array.from(
        listicle.querySelectorAll("input.listicle-voting-input[type=radio]")
    );

    const reactions = allOptions.map(getInputValue).reduce(reactionReducer, {});
    reactions[selected].positive = 1;
    return reactions;
}

const executeAction = curry(function executeAction(listicle, context, element) {
    const action = element.getAttribute("action");
    if (!(action in pollActions)) {
        return;
    }

    pollActions[action](listicle, context, element);
});

const onVoteSuccess = curry(function onVoteSuccess(
    listicle,
    context,
    submitButton,
    form,
    serverPayload
) {
    submitButton.disabled = false;

    form.querySelectorAll("input[action],select[action]").forEach(executeAction(listicle, context));

    listicle.dispatchEvent(
        new CustomEvent(EVENTS.VOTE_SUCCESS, {
            detail: { message: "Thank you for taking the time to complete this survey." },
        })
    );
    setVoteRecord(listicle, serverPayload.vote.voter_id);
    form.classList.add("hidden");
    listicle.dispatchEvent(
        new CustomEvent(EVENTS.DISPLAY_RESULTS, {
            detail: serverPayload,
        })
    );
});

const onVoteError = curry(function onVoteError(submitButton, emitter, error) {
    submitButton.disabled = false;
    emitter(error);
});

function hideElement(element) {
    element.classList.add("hidden");
}

const onSubmit = curry(function onSubmit(
    listicle,
    context,
    form,
    submitButton,
    errorEmitter,
    event
) {
    event.preventDefault();

    clearFeedback(listicle);
    form.querySelectorAll(".listicle-voting-input-error").forEach(hideElement);

    const isValid = form.checkValidity();

    if (!isValid) {
        errorEmitter({ message: "Please correct any required fields and submit again" });
        return;
    }

    submitButton.disabled = true;

    const { listicleVotingChoice, ...payload } = getFormBody(listicle);
    const body = {
        payload,
        reactions: reactionsFactory(listicleVotingChoice, listicle),
    };
    const voterID = getVoteRecord(listicle);

    vote(window.REBELMOUSE_BOOTSTRAP_DATA.runner.id, listicle.dataset.postId, voterID, body)
        .then(onVoteSuccess(listicle, context, submitButton, event.target))
        .catch(onVoteError(submitButton, errorEmitter));
});

const reloadForm = curry(function reloadForm(form, _event) {
    form.classList.remove("hidden");
});

function findErrorHolder(element) {
    return element.classList.contains("listicle-voting-input-error");
}

function getErrorElementFor(inputElement) {
    if (inputElement.classList.contains("listicle-voting-extra-field-input")) {
        return inputElement
            .closest(".listicle-voting-extra-field-label")
            .querySelector(".listicle-voting-input-error");
    } else {
        return inputElement
            .closest("form.listicle-voting-form")
            .querySelector(".listicle-voting-question-error-holder .listicle-voting-input-error");
    }
}

function onInvalidInput(event) {
    const inputElement = this;
    const errorElement = getErrorElementFor(inputElement);
    errorElement.classList.remove("hidden");
    errorElement.textContent = "This question is required";
}

function setValidationMessage(inputElement) {
    inputElement.addEventListener("invalid", onInvalidInput);
}

export default function prepare({ listicle, context }) {
    const form = listicle.querySelector("form");
    const submitButton = form.querySelector("button.listicle-item-voting-action-submit");

    form.noValidate = true;

    prepareExtraFields(form);

    form.querySelectorAll("input,select").forEach(setValidationMessage);
    form.addEventListener(
        "submit",
        onSubmit(listicle, context, form, submitButton, emitErrorFactory(listicle)),
        {
            capture: true,
        }
    );
    listicle.addEventListener(EVENTS.VOTE_AGAIN, reloadForm(form));

    if (getVoteRecord(listicle)) {
        form.classList.add("hidden");
        submitButton.textContent = "Update vote";
    }

    return { listicle, context };
}
